/**
 * Panel-wide error boundary.
 *
 * Without one, any render error unmounts the whole SPA and leaves a blank page —
 * the operator sees nothing and the only clue lives in the browser console. This
 * catches the error, shows it, and offers a way back, so a broken screen stays a
 * broken screen instead of a broken panel.
 */
import { Alert, Button, Code, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Component, type ContextType, type ErrorInfo, type ReactNode } from 'react';

import { I18nContext } from '../i18n';

type Props = { children: ReactNode };
type State = { error: Error | null; stack: string | null };

export class ErrorBoundary extends Component<Props, State> {
  // A boundary has to be a class, so it reads the locale from the context
  // directly rather than through `useT()`.
  static override contextType = I18nContext;
  declare context: ContextType<typeof I18nContext>;

  override state: State = { error: null, stack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep the console trail — the boundary shows a summary, not everything.
    console.error('Admin panel render error:', error, info.componentStack);
    this.setState({ stack: info.componentStack ?? null });
  }

  reset = () => {
    this.setState({ error: null, stack: null });
  };

  override render() {
    const { error, stack } = this.state;
    if (!error) return this.props.children;

    // If the boundary somehow catches before the provider mounts, fall back to
    // the English catalog rather than rendering nothing.
    const t = this.context?.t ?? ((key: string) => key);

    return (
      <Stack gap="md" p="lg">
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
          title={t('errorBoundary.title')}
        >
          <Text size="sm" mb="sm">
            {error.message}
          </Text>
          {stack ? (
            <Code block style={{ maxHeight: 260, overflow: 'auto', fontSize: 11 }}>
              {stack.trim()}
            </Code>
          ) : null}
        </Alert>
        <Group>
          <Button variant="default" onClick={this.reset}>
            {t('errorBoundary.retry')}
          </Button>
          <Button
            variant="subtle"
            onClick={() => {
              window.location.assign('/');
            }}
          >
            {t('errorBoundary.home')}
          </Button>
        </Group>
      </Stack>
    );
  }
}
